import { cn } from 'cn';
import { pageRange, type QuotedGroup, type RailSection } from './outline';

/**
 * The khutbah outline: a 200px rail between the icon rail and the reader.
 *
 * A khutbah has a fixed shape — Opening, First khutbah, Second khutbah,
 * Supplication — so the reader shows it rather than making someone scroll to
 * find out where they are. Below the sections, **Quoted** lists the Qur'an
 * verses and hadith the matcher found, which is the fastest way to reach an
 * ayah someone mentioned.
 *
 * <p>The current section follows the scroll position and is marked with
 * {@code aria-current}, so a screen reader hears the same thing a sighted
 * reader sees highlighted.
 */

export function SectionsRail({
  sections,
  quoted,
  activeSectionType,
  onJump,
  quotedFilter,
  onQuotedFilter,
}: {
  sections: readonly RailSection[];
  quoted: readonly QuotedGroup[];
  activeSectionType: string | null;
  onJump: (passageId: string) => void;
  quotedFilter: 'quran' | 'hadith' | null;
  onQuotedFilter: (kind: 'quran' | 'hadith' | null) => void;
}) {
  return (
    <nav
      aria-label="Khutbah outline"
      className={cn(
        'hidden w-50 shrink-0 flex-col gap-1 border-e border-sidebar-border',
        'bg-sidebar px-2 py-4 lg:flex',
      )}
    >
      <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Sections
      </p>

      {sections.map((section) => {
        const current = section.sectionType === activeSectionType;
        const range = pageRange(section);
        return (
          <button
            key={section.anchorPassageId}
            type="button"
            // Heard by a screen reader as the current location, not merely
            // shown highlighted.
            aria-current={current ? 'true' : undefined}
            onClick={() => onJump(section.anchorPassageId)}
            className={cn(
              'flex flex-col items-start rounded-md px-2 py-2 text-start',
              'min-h-9 hover:bg-sidebar-accent',
              current ? 'bg-sidebar-accent text-sidebar-primary' : 'text-foreground',
            )}
          >
            <span className="text-sm">{section.label}</span>
            {range ? (
              <span className="text-[11px] text-muted-foreground">{range}</span>
            ) : null}
          </button>
        );
      })}

      {quoted.length > 0 ? (
        <>
          <p className="mt-4 px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Quoted
          </p>
          {quoted.map((group) => {
            const filtered = quotedFilter === group.kind;
            return (
              <button
                key={group.kind}
                type="button"
                aria-pressed={filtered}
                // Filters the reader to just these passages, which is the
                // fastest way to find the ayah someone mentioned.
                onClick={() => onQuotedFilter(filtered ? null : group.kind)}
                className={cn(
                  'flex min-h-9 items-center justify-between rounded-md px-2 py-2',
                  'hover:bg-sidebar-accent',
                  filtered ? 'bg-sidebar-accent text-sidebar-primary' : 'text-foreground',
                )}
              >
                <span className="text-sm">{group.label}</span>
                <span className="text-[11px] text-muted-foreground">
                  {group.passages.length}
                </span>
              </button>
            );
          })}
          {quotedFilter ? (
            <button
              type="button"
              onClick={() => onQuotedFilter(null)}
              className="mt-1 px-2 py-1 text-start text-[11px] text-primary underline"
            >
              Show the whole khutbah
            </button>
          ) : null}
        </>
      ) : null}
    </nav>
  );
}
